import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface Anime {
  id: number;
  title: string;
  description: string;
  coverUrl: string;
  gifUrls?: string[]; // Optional array of GIF URLs
  episodes: number | null;
  status: string;
  year: number | null;
  genres: string[];
  averageScore: number | null;
}

const FETCH_DELAY_MS = 1000;

const AnimeTile: React.FC = () => {
  const [recommendations, setRecommendations] = useState<Anime[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hoveredAnime, setHoveredAnime] = useState<Anime | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null);
  const [currentGifIndices, setCurrentGifIndices] = useState<{ [key: number]: number }>({});
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const autoCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const hoverDelayTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Anime data with custom GIFs
  const animeData = React.useMemo(
    () => [
      {
        id: 11061, // Hunter x Hunter
        gifUrls: [
          '/content/hunter-x-hunter-gifs/hxh-1.gif',
          '/content/hunter-x-hunter-gifs/hxh-2.gif',
          '/content/hunter-x-hunter-gifs/hxh-3.gif',
          '/content/hunter-x-hunter-gifs/hxh-4.gif',
          '/content/hunter-x-hunter-gifs/hxh-5.gif'
        ]
      },
      {
        id: 16498, // Attack on Titan
        gifUrls: [
          '/content/attack-on-titan-gifs/aot-1.gif',
          '/content/attack-on-titan-gifs/aot-2.gif',
          '/content/attack-on-titan-gifs/aot-3.gif',
          '/content/attack-on-titan-gifs/aot-4.gif',
          '/content/attack-on-titan-gifs/aot-5.gif'
        ]
      },
      {
        id: 37999, // Kaguya-sama: Love is War
        gifUrls: [
          '/content/love-is-war-gifs/liw-1.gif',
          '/content/love-is-war-gifs/liw-2.gif',
          '/content/love-is-war-gifs/liw-3.gif'
        ]
      },
      {
        id: 52991, // Frieren
        gifUrls: [
          '/content/frieren-gifs/frieren-1.gif',
          '/content/frieren-gifs/frieren-2.gif',
          '/content/frieren-gifs/frieren-3.gif',
          '/content/frieren-gifs/frieren-4.gif'
        ]
      },
      {
        id: 38671, // Fire Force
        gifUrls: [
          '/content/fire-force-gifs/ff-1.gif',
          '/content/fire-force-gifs/ff-2.gif',
          '/content/fire-force-gifs/ff-3.gif',
          '/content/fire-force-gifs/ff-4.gif'
        ]
      },
      {
        id: 1575, // Code Geass
        gifUrls: [
          '/content/code-geass-gifs/cg-1.gif',
          '/content/code-geass-gifs/cg-2.gif',
          '/content/code-geass-gifs/cg-3.gif',
          '/content/code-geass-gifs/cg-4.gif',
          '/content/code-geass-gifs/cg-5.gif'
        ]
      },
      {
        id: 37521, // Vinland Saga
        gifUrls: [
          '/content/vinland-saga-gifs/anime-vinland-saga.gif',
          '/content/vinland-saga-gifs/tomuda.gif',
          '/content/vinland-saga-gifs/thorkell-torukeru.gif',
          '/content/vinland-saga-gifs/thorfinn.gif',
          '/content/vinland-saga-gifs/canute-king-canute.gif'
        ]
      },
      {
        id: 32182, // Mob Psycho 100
        gifUrls: [
          '/content/mob-psycho-gifs/mob-1.gif',
          '/content/mob-psycho-gifs/mob-2.gif',
          '/content/mob-psycho-gifs/mob-3.gif',
          '/content/mob-psycho-gifs/mob-4.gif'
        ]
      }
    ],
    []
  );

  useEffect(() => {
    const fetchAnime = async (): Promise<Anime[]> => {
      const results: (Anime | null)[] = [];

      // Fetch sequentially with delay to respect Jikan API rate limits.
      // Was 500ms (2 req/s) — bumped to 1000ms (1 req/s), comfortably under
      // Jikan's documented 3 req/s limit, since 500ms was still occasionally
      // tight enough to get individual requests rate-limited and dropped.
      for (const item of animeData) {
        try {
          console.log(`Fetching anime ID: ${item.id}`);
          const response = await fetch(`https://api.jikan.moe/v4/anime/${item.id}`);

          if (!response.ok) {
            console.error(`HTTP error for anime ${item.id}! status: ${response.status}`);
            results.push(null);
            await new Promise(resolve => setTimeout(resolve, FETCH_DELAY_MS));
            continue;
          }

          const result = await response.json();
          const anime = result.data;

          // Truncate synopsis
          let description = anime.synopsis || 'No description available.';
          if (description.length > 150) {
            description = description.substring(0, 150) + '...';
          }

          results.push({
            id: anime.mal_id,
            title: anime.title_english || anime.title,
            description,
            coverUrl: anime.images.jpg.large_image_url,
            gifUrls: item.gifUrls,
            episodes: anime.episodes,
            status: anime.status.toLowerCase(),
            year: anime.year || (anime.aired?.from ? new Date(anime.aired.from).getFullYear() : null),
            genres: anime.genres.slice(0, 5).map((g: { name: string }) => g.name),
            averageScore: anime.score ? Math.round(anime.score * 10) : null,
          });

          console.log(`Successfully fetched: ${anime.title}`);
          await new Promise(resolve => setTimeout(resolve, FETCH_DELAY_MS));
        } catch (error) {
          console.error(`Error fetching anime ${item.id}:`, error);
          results.push(null);
          await new Promise(resolve => setTimeout(resolve, FETCH_DELAY_MS));
        }
      }

      return results.filter((anime): anime is Anime => anime !== null);
    };

    const loadAnime = async () => {
      try {
        setLoading(true);
        const fetchedAnime = await fetchAnime();
        setRecommendations(fetchedAnime);
      } catch (error) {
        console.error('Error loading anime:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAnime();
  }, [animeData]);

  // Cycle through GIFs using intervals
  useEffect(() => {
    if (recommendations.length === 0) return;

    const intervals: NodeJS.Timeout[] = [];

    recommendations.forEach(anime => {
      if (anime.gifUrls && anime.gifUrls.length > 1) {
        // Cycle through GIFs every 3 seconds
        const interval = setInterval(() => {
          setCurrentGifIndices(prev => ({
            ...prev,
            [anime.id]: ((prev[anime.id] || 0) + 1) % anime.gifUrls!.length
          }));
        }, 3000);

        intervals.push(interval);
      }
    });

    return () => {
      intervals.forEach(interval => clearInterval(interval));
    };
  }, [recommendations]);

  const handleMouseEnter = (anime: Anime, event: React.MouseEvent<HTMLDivElement>) => {
    // Clear any existing timeouts
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    if (hoverDelayTimeoutRef.current) {
      clearTimeout(hoverDelayTimeoutRef.current);
      hoverDelayTimeoutRef.current = null;
    }

    // Capture position immediately before timeout
    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 340;
    const spacing = 16;

    let left = rect.right + spacing;
    let top = rect.top;

    if (left + popupWidth > window.innerWidth) {
      left = rect.left - popupWidth - spacing;
    }

    if (left < spacing) {
      left = spacing;
    }

    const popupHeight = 400;
    if (top + popupHeight > window.innerHeight) {
      top = Math.max(spacing, window.innerHeight - popupHeight - spacing);
    }

    // Wait 0.5 seconds before showing popup
    hoverDelayTimeoutRef.current = setTimeout(() => {
      console.log('Anime popup position:', { top, left, anime: anime.title });
      setHoveredAnime(anime);
      setPopupPosition({ top, left });

      // Auto-close after 5 seconds
      autoCloseTimeoutRef.current = setTimeout(() => {
        setHoveredAnime(null);
        setPopupPosition(null);
      }, 5000);
    }, 500);
  };

  const handleMouseLeave = () => {
    // Clear hover delay if user leaves before popup shows
    if (hoverDelayTimeoutRef.current) {
      clearTimeout(hoverDelayTimeoutRef.current);
      hoverDelayTimeoutRef.current = null;
    }

    // Start timeout to close popup after 300ms
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredAnime(null);
      setPopupPosition(null);
    }, 300);
  };

  const handlePopupMouseEnter = () => {
    // Clear close timeout when entering popup
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Clear auto-close timeout when entering popup
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    // Restart auto-close timer
    autoCloseTimeoutRef.current = setTimeout(() => {
      setHoveredAnime(null);
      setPopupPosition(null);
    }, 5000);
  };

  const handlePopupMouseLeave = () => {
    // Close popup when leaving it
    setHoveredAnime(null);
    setPopupPosition(null);
  };

  if (loading) {
    return (
      <div className="bento-item bento-anime">
        <div className="widget-content">
          <div className="anime-loading">Loading anime...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bento-item bento-anime">
      <div className="widget-content anime-widget">
        <div className="anime-header-section">
          <div className="anime-header-with-icon">
            <h3 className="anime-title">Nippon's curation</h3>
          </div>
        </div>

        <div className="anime-content">
          {/* Recommendations Grid - 3x2 */}
          <div className="anime-section">
            <p className="anime-section-label">Recommendations</p>
            <div className="anime-grid">
              {recommendations.map((anime) => (
                <div
                  key={anime.id}
                  className="anime-card"
                  onMouseEnter={(e) => handleMouseEnter(anime, e)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="anime-cover-wrapper">
                    <img
                      src={
                        anime.gifUrls && anime.gifUrls.length > 0
                          ? anime.gifUrls[currentGifIndices[anime.id] || 0]
                          : anime.coverUrl
                      }
                      alt={anime.title}
                      className={`anime-cover ${anime.id === 52991 ? 'anime-cover-zoomed' : ''}`}
                      loading="lazy"
                    />
                  </div>
                  <p className="anime-card-title">{anime.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Anime Popup - Rendered via Portal */}
      {hoveredAnime && popupPosition && (() => {
        console.log('Rendering anime popup for:', hoveredAnime.title, popupPosition);
        return ReactDOM.createPortal(
          <div
            className="anime-popup"
            style={{
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
            }}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
          >
            <h3 className="anime-popup-title">{hoveredAnime.title}</h3>
            <div className="anime-popup-meta">
              {hoveredAnime.year && (
                <span className="anime-popup-year">{hoveredAnime.year}</span>
              )}
              {hoveredAnime.episodes && (
                <span className="anime-popup-episodes">{hoveredAnime.episodes} eps</span>
              )}
              {hoveredAnime.averageScore && (
                <span className="anime-popup-score">⭐ {hoveredAnime.averageScore}%</span>
              )}
            </div>
            <div className="anime-popup-genres">
              {hoveredAnime.genres.map((genre) => (
                <span key={genre} className="anime-popup-genre">{genre}</span>
              ))}
            </div>
            <p className="anime-popup-description">{hoveredAnime.description}</p>
            <a
              href={`https://myanimelist.net/anime/${hoveredAnime.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="anime-popup-link"
            >
              <span className="anime-link-text">View on</span>
              <span className="mal-wordmark">MyAnimeList</span>
            </a>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default AnimeTile;
